import { useMutation, UseMutationOptions } from 'react-query';
import { AxiosRequestConfig } from 'axios';
import { apiOTPClient } from './apiClient';
import { axiosInstance, axiosInstance2 } from './AxiosInstance';

export const useApiMutation = <T, U>(
    url: string,
    method: 'get' | 'post' | 'put' | 'delete' | 'patch',
    options?: UseMutationOptions<
        T,
        Error,
        { data: U; params?: Record<string, any>; url?: string }
    >
) => {
    const mutationFn = async (variables: {
        data: U;
        params?: Record<string, any>;
        url?: string;
    }) => {
        const axiosConfig: AxiosRequestConfig = {
            method,
            url: variables.url || url,
            data: variables.data,
            params: variables.params,
            headers: {
                'Content-Type': 'application/json'
            }
        };

        const response = await axiosInstance(axiosConfig);
        return response.data as T;
    };

    return useMutation<
        T,
        Error,
        { data: U; params?: Record<string, any>; url?: string }
    >(mutationFn, {
        mutationKey: [url, method],
        ...options
    });
};

interface OTPResponse {
    statusCode: number;
    success: boolean;
    data?: any;
    message?: string;
    valid?: boolean;
}

export const OTPVerifications = async (url: string): Promise<OTPResponse> => {
    try {
        const response = await apiOTPClient.post<OTPResponse>(url, '');
        return response;
    } catch (error: any) {
        const message =
            error.response?.data?.message ||
            error.message ||
            'Unknown error occurred';
        const statusCode = error.response?.status || 500;

        throw { message, statusCode };
    }
};

export const useApiMutation2 = <T, U>(
    url: string,
    method: 'get' | 'post' | 'put' | 'delete',
    options?: UseMutationOptions<
        T,
        Error,
        { data: U; params?: Record<string, any> }
    >
) => {
    const mutationFn = async (variables: {
        data: U;
        params?: Record<string, any>;
    }) => {
        const axiosConfig: AxiosRequestConfig = {
            method,
            url,
            data: variables.data,
            params: variables.params, // Pass query parameters as params
            headers: {
                'Content-Type': 'application/x-www-form-urlencoded' // Set content type if not FormData
            }
        };

        const response = await axiosInstance2(axiosConfig);
        return response?.data;
    };

    return useMutation<T, Error, { data: U; params?: Record<string, any> }>(
        mutationFn,
        {
            mutationKey: [url, method],
            ...options
        }
    );
};

export const useApiAuthMutationDynamic = <T, U>(
    url: string,
    method: 'get' | 'post' | 'put' | 'delete' | 'patch',
    options?: UseMutationOptions<
        T,
        Error,
        { data: U; params?: Record<string, any>; token?: string }
    >
) => {
    const mutationFn = async (variables: {
        data: U;
        params?: Record<string, any>;
        token?: string;
    }) => {
        const headers: Record<string, string> = {
            'Content-Type': 'application/json' // default for normal APIs
        };

        // ✅ Add Authorization header if token is provided
        if (variables.token) {
            headers.Authorization = `Bearer ${variables.token}`;
        }

        const axiosConfig: AxiosRequestConfig = {
            method,
            url,
            data: variables.data,
            params: variables.params,
            headers,
            withCredentials: false
        };

        const response = await axiosInstance(axiosConfig);
        // const response = await fetch(url, {
        //   method,
        //   body: JSON.stringify(variables.data),
        //   headers,
        //   credentials: 'omit'
        // });

        return response as T;
    };

    return useMutation<
        T,
        Error,
        { data: U; params?: Record<string, any>; token?: string }
    >(mutationFn, {
        mutationKey: [url, method],
        ...options
    });
};
